import { conversationsApi } from "@/services/conversation-service";
import { Conversations } from "@/types";
import { useQuery } from "@tanstack/react-query";
import { isToday, isYesterday, startOfDay, subDays } from "date-fns";
import { muiColors } from "@/theme/designSystem";
import { PlaygroundListItem } from "./playground-list-item";
import { useLocation } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useProjects } from "@/contexts/ProjectContext";
// requestScope.js is plain JS, so this import is implicitly typed `any`.
import { getActiveOrganizationId } from "@/utils/requestScope";

type ConversationGroupKey = "today" | "yesterday" | "last7Days" | "older";

const groupConversationsByDate = async (
  search: string,
): Promise<Record<ConversationGroupKey, Conversations>> => {
  const data = await conversationsApi.getConversations({
    search: search,
    include_archived: false,
    skip: 0,
    limit: 50,
  });
  const now = new Date();
  const startOfToday = startOfDay(now);
  const startOfYesterday = startOfDay(subDays(now, 1));
  const sevenDaysAgo = startOfDay(subDays(now, 7));
  const groupedDataByDate = data.reduce(
    (acc, current) => {
      const updated_at = new Date(current.updated_at);
      if (isToday(updated_at)) {
        acc.today.push(current);
      } else if (isYesterday(updated_at)) {
        acc.yesterday.push(current);
      } else if (updated_at >= sevenDaysAgo && updated_at < startOfYesterday)
        acc.last7Days.push(current);
      else acc.older.push(current);

      return acc;
    },
    {
      today: [],
      yesterday: [],
      last7Days: [],
      older: [],
    },
  );
  return groupedDataByDate;
};

export const PlaygroundConversations = ({ search }: { search: string }) => {
  const { t } = useTranslation();
  const { selectedProjects } = useProjects();
  const projectScope =
    selectedProjects.length === 1 && selectedProjects[0] !== "all"
      ? selectedProjects[0]
      : "all";
  // Conversations come back scoped to the org header, and staleTime is
  // Infinity, so without the org id in the key a switch keeps serving the
  // previous org's list for the life of the page. Mirror the header's own
  // source: context once loaded, localStorage during the initial load, and
  // "personal" when neither is set (personal workspace sends no header).
  const { currentOrganization } = useOrganization();
  const orgScope =
    currentOrganization?.id ?? getActiveOrganizationId() ?? "personal";
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["conversations", orgScope, search, projectScope],
    queryFn: () => groupConversationsByDate(search),
    staleTime: Infinity,
  });
  const location = useLocation();
  if (isLoading || isFetching)
    return (
      <div className="py-4 flex flex-col gap-3">
        {Array.from({ length: 5 }, () => 0).map(() => (
          <Skeleton className="p-2  hover:bg-[var(--color-29)] cursor-pointer rounded-[8px]" />
        ))}
      </div>
    );
  const path = location.pathname.split("/");
  return (
    <div
      style={{
        padding: "16px 0",
      }}
      className="flex flex-col gap-3"
    >
      {data &&
        Object.entries(data).map((conversationEntry) => (
          <div key={conversationEntry[0]} className="flex flex-col gap-2">
            {conversationEntry[1].length !== 0 && (
              <span className="text-[14.8px] text-foreground opacity-70 font-bold pt-1 pr-0 pb-1 pl-2 rtl:pr-2 rtl:pl-0">
                {t(`conversation.${conversationEntry[0]}`)}
              </span>
            )}
            {conversationEntry[1].map((conversation) => (
              <PlaygroundListItem
                title={conversation.name}
                id={conversation.id}
                key={conversation.id}
                active={conversation.id === path[2]}
              />
            ))}
          </div>
        ))}
    </div>
  );
};
